import { useEffect, useState } from 'react';
import { fetchTechnologies, fetchProjects } from '../../services/api';
import {
  createTechnologyCategory,
  updateTechnologyCategory,
  deleteTechnologyCategory,
  reorderTechnologyCategories,
  createTechnology,
  updateTechnology,
  deleteTechnology,
  fetchTechnologyUsage,
  linkTechnologyProject,
  unlinkTechnologyProject
} from '../../services/adminApi';
import { useCrudResource } from '../../hooks/useCrudResource';
import Button from '../../components/admin/ui/Button';
import Loader from '../../components/admin/ui/Loader';
import EntityTable from '../../components/admin/EntityTable';
import EntityForm from '../../components/admin/EntityForm';
import EntityFormPage from '../../components/admin/EntityFormPage';
import { technologyCategoryColumns, technologyCategoryFields, getTechnologyColumns, getTechnologyFields } from '../../config/admin/technologies.config';

const fetchTechnologyCategories = () => fetchTechnologies().then((data) => data?.categories ?? []);
const fetchTechnologyItems = () => fetchTechnologies().then((data) => data?.items ?? []);

function AdminTechnologiesPage({ showToast }) {
  const {
    items: categories,
    loading: categoriesLoading,
    reload: reloadCategories,
    createItem: createCategoryItem,
    updateItem: updateCategoryItem,
    removeItem: removeCategoryItem
  } = useCrudResource(
    {
      fetchAll: fetchTechnologyCategories,
      create: createTechnologyCategory,
      update: updateTechnologyCategory,
      remove: deleteTechnologyCategory
    },
    { showToast, entityLabel: 'Catégorie' }
  );

  const {
    items: technologies,
    loading: technologiesLoading,
    createItem: createTechnologyItem,
    updateItem: updateTechnologyItem,
    removeItem: removeTechnologyItem
  } = useCrudResource(
    {
      fetchAll: fetchTechnologyItems,
      create: createTechnology,
      update: updateTechnology,
      remove: deleteTechnology
    },
    { showToast, entityLabel: 'Technologie' }
  );

  const [activeTab, setActiveTab] = useState('categories');
  const [view, setView] = useState('list');
  const [editingKind, setEditingKind] = useState(null);
  const [editing, setEditing] = useState(null);

  const openCreateCategory = () => {
    setEditingKind('category');
    setEditing(null);
    setView('form');
  };

  const openEditCategory = (category) => {
    setEditingKind('category');
    setEditing(category);
    setView('form');
  };

  const handleCategoryDelete = async (category) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    try {
      await removeCategoryItem(category.id);
    } catch (error) {
      showToast('Impossible de supprimer : catégorie encore utilisée par des technologies.');
    }
  };

  const handleReorderCategories = async (ids) => {
    await reorderTechnologyCategories(ids);
    await reloadCategories();
  };

  const openCreateTechnology = () => {
    setEditingKind('technology');
    setEditing(null);
    setView('form');
  };

  const openEditTechnology = (technology) => {
    setEditingKind('technology');
    setEditing(technology);
    setView('form');
  };

  const handleTechnologyDelete = async (technology) => {
    if (!window.confirm('Supprimer cette technologie ?')) return;
    try {
      await removeTechnologyItem(technology.id);
    } catch (error) {
      showToast(error.message || 'Impossible de supprimer cette technologie.');
    }
  };

  const handleFormSubmit = async (values) => {
    if (editingKind === 'category') {
      if (editing) {
        await updateCategoryItem(editing.id, values);
      } else {
        await createCategoryItem(values);
      }
    } else if (editing) {
      await updateTechnologyItem(editing.id, values);
    } else {
      await createTechnologyItem(values);
    }
    setView('list');
  };

  if (categoriesLoading || technologiesLoading) return <Loader label="Chargement des technologies..." />;

  if (view === 'form') {
    const isCategory = editingKind === 'category';
    return (
      <EntityFormPage
        listLabel="Technologies"
        itemLabel={editing ? editing.label?.fr || editing.label?.en : null}
        title={
          isCategory
            ? editing
              ? 'Modifier la catégorie'
              : 'Ajouter une catégorie'
            : editing
            ? 'Modifier la technologie'
            : 'Ajouter une technologie'
        }
        onBack={() => setView('list')}
      >
        {isCategory ? (
          <EntityForm
            key={editing?.id ?? 'new'}
            fields={technologyCategoryFields}
            initialValues={editing || {}}
            onSubmit={handleFormSubmit}
            submitLabel={editing ? 'Sauvegarder' : 'Créer'}
          />
        ) : (
          <>
            <EntityForm
              key={editing?.id ?? 'new'}
              fields={getTechnologyFields(categories)}
              initialValues={editing || {}}
              onSubmit={handleFormSubmit}
              submitLabel={editing ? 'Sauvegarder' : 'Créer'}
            />
            {editing && <TechnologyUsagePanel technologyId={editing.id} showToast={showToast} />}
          </>
        )}
      </EntityFormPage>
    );
  }

  return (
    <section className="admin-section">
      <h2>Technologies</h2>

      <div className="admin-subtabs">
        <button
          type="button"
          className={`admin-subtab-btn ${activeTab === 'categories' ? 'active' : ''}`}
          onClick={() => setActiveTab('categories')}
        >
          Catégories <span className="admin-subtab-count">({categories.length})</span>
        </button>
        <button
          type="button"
          className={`admin-subtab-btn ${activeTab === 'items' ? 'active' : ''}`}
          onClick={() => setActiveTab('items')}
        >
          Technologies <span className="admin-subtab-count">({technologies.length})</span>
        </button>
      </div>

      {activeTab === 'categories' ? (
        <>
          <div className="admin-actions" style={{ justifyContent: 'flex-end', marginBottom: 'var(--space-3)' }}>
            <Button onClick={openCreateCategory}>+ Ajouter une catégorie</Button>
          </div>
          <EntityTable
            columns={technologyCategoryColumns}
            rows={categories}
            onEdit={openEditCategory}
            onDelete={handleCategoryDelete}
            onReorder={handleReorderCategories}
            emptyMessage="Aucune catégorie."
          />
        </>
      ) : (
        <>
          <div className="admin-actions" style={{ justifyContent: 'flex-end', marginBottom: 'var(--space-3)' }}>
            <Button onClick={openCreateTechnology}>+ Ajouter une technologie</Button>
          </div>
          <EntityTable
            columns={getTechnologyColumns(categories)}
            rows={technologies}
            onEdit={openEditTechnology}
            onDelete={handleTechnologyDelete}
            emptyMessage="Aucune technologie."
            searchPlaceholder="Rechercher une technologie..."
            searchFilter={(row, needle) =>
              (row.label?.fr || '').toLowerCase().includes(needle) || (row.label?.en || '').toLowerCase().includes(needle)
            }
            categoryFilter={{
              options: categories.map((cat) => ({ value: cat.id, label: cat.label.fr })),
              getValue: (row) => row.categoryId
            }}
          />
        </>
      )}
    </section>
  );
}

function TechnologyUsagePanel({ technologyId, showToast }) {
  const [allProjects, setAllProjects] = useState([]);
  const [usageIds, setUsageIds] = useState([]);
  const [usageLoading, setUsageLoading] = useState(true);

  useEffect(() => {
    fetchProjects().then(setAllProjects);
  }, []);

  useEffect(() => {
    setUsageLoading(true);
    fetchTechnologyUsage(technologyId)
      .then((usage) => setUsageIds(usage.map((p) => p.id)))
      .finally(() => setUsageLoading(false));
  }, [technologyId]);

  const toggle = async (projectId, checked) => {
    setUsageIds((ids) => (checked ? [...ids, projectId] : ids.filter((id) => id !== projectId)));
    try {
      if (checked) {
        await linkTechnologyProject(technologyId, projectId);
      } else {
        await unlinkTechnologyProject(technologyId, projectId);
      }
    } catch (error) {
      setUsageIds((ids) => (checked ? ids.filter((id) => id !== projectId) : [...ids, projectId]));
      showToast(error.message || 'Erreur lors de la mise à jour.');
    }
  };

  return (
    <div className="admin-field">
      <span>Projets utilisant cette technologie</span>
      {usageLoading ? (
        <Loader label="Chargement des projets..." />
      ) : (
        <div className="admin-multiselect">
          {allProjects.map((project) => (
            <label key={project.id}>
              <input
                type="checkbox"
                checked={usageIds.includes(project.id)}
                onChange={(e) => toggle(project.id, e.target.checked)}
              />
              {project.nom?.fr}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminTechnologiesPage;
